import React from "react";
import produce from "immer";

import { useMessage } from "@/components/Message";
import {
  type VerificationService,
  type Country,
  type FormSelectChoice,
  type ViewModel,
  type ErrorId,
  type FieldId,
  type WithOrganization,
} from "@/lib/types/types";
import {
  updateFieldValidationErrorsByFieldId,
  produceDraftViewModel,
  updateFieldValidationErrors,
  getFieldConfigByFieldId,
} from "@/lib/utils/stepComponentHelpers/stepComponentHelpers";
import { validateFieldById } from "@/lib/validators/validators";
import { blankOrganization } from "@/lib/types/empties";
import { useProgramTheme } from "@/lib/state/contextHooks";
import { getConfiguredCountries } from "@/lib/ProgramTheme/programThemeGetters";

import { CountryComponent } from "./CountryComponent";

type CountryComponentWrapperProps = {
  verificationService: VerificationService;
  viewModelDraftDecorator?: (
    draft: ViewModel,
    countryChoice?: FormSelectChoice<Country, string>,
  ) => void;
  disabled?: boolean;
};

export const CountryComponentWrapper = ({
  verificationService,
  viewModelDraftDecorator = undefined,
  disabled = false,
}: CountryComponentWrapperProps) => {
  const programTheme = useProgramTheme();
  const { i18n, msg } = useMessage();

  const availableCountries: Country[] = getConfiguredCountries(programTheme);
  const localeAwareSorter = (a, b) => Intl.Collator(i18n.language).compare(a.label, b.label);
  const countryChoices = availableCountries
    .map((countryCode) => ({
      value: countryCode,
      label: msg({ id: `countries.${countryCode}` }),
    }))
    .sort(localeAwareSorter);

  const { fieldValidationErrors } = verificationService;
  const hasMultipleCountries = countryChoices.length > 1;
  const isFieldHidden = getFieldConfigByFieldId("country")?.hidden;

  const updateCountryChoice = (countryChoice?: FormSelectChoice<Country, string>) => {
    const nextState: ViewModel = produce(
      verificationService.viewModel,
      (draft: ViewModel & WithOrganization) => {
        draft.countryChoice = countryChoice || undefined;

        // Adds backwards compatibility with Age and Employment programs
        (draft as any).country = countryChoice ? countryChoice.value : undefined;
        if (viewModelDraftDecorator) {
          viewModelDraftDecorator(draft, countryChoice);
        }

        // Reset to a blank org when changing the country choice
        if (draft.organization) {
          draft.organization = blankOrganization.organization;
        }
      },
    );
    verificationService.updateViewModel(nextState);
  };

  React.useEffect(() => {
    const includesUS = countryChoices.some((choice) => choice.value === "US");
    if (!hasMultipleCountries && !includesUS) {
      const fieldsToSkip: FieldId[] = verificationService.viewModel.fieldsToSkipValidation
        ? [...verificationService.viewModel.fieldsToSkipValidation, "country"]
        : ["country", "address1", "postalCode", "state", "city"];
      verificationService.updateViewModel(
        produceDraftViewModel(
          verificationService.viewModel,
          "fieldsToSkipValidation",
          fieldsToSkip,
        ),
      );
    }
  }, [hasMultipleCountries]);

  React.useEffect(() => {
    const updatedFieldValidationErrors = produce(
      verificationService.fieldValidationErrors,
      (draft) => {
        if (draft.country) {
          draft.country = validateFieldById(
            "country",
            verificationService.viewModel.countryChoice,
            verificationService.formValidationOptions,
          ) as ErrorId;
        }
        if (
          "postalCode" in verificationService.viewModel &&
          verificationService.viewModel.postalCode
        ) {
          draft.postalCode = validateFieldById(
            "postalCode",
            verificationService.viewModel.postalCode,
            verificationService.formValidationOptions,
          ) as ErrorId;
        }
      },
    );
    updateFieldValidationErrors(updatedFieldValidationErrors, verificationService);
  }, [verificationService.viewModel.countryChoice]);

  if (!hasMultipleCountries || isFieldHidden) {
    return null;
  }

  return (
    <CountryComponent
      isRequired
      options={countryChoices}
      value={verificationService.viewModel.countryChoice}
      isErrored={Boolean(fieldValidationErrors.country)}
      disabled={disabled}
      onChange={async (countryChoice: FormSelectChoice<Country, string>) => {
        const countryValue = countryChoice ? countryChoice.value : undefined;
        updateCountryChoice(countryChoice);
        updateFieldValidationErrorsByFieldId("country", countryValue, verificationService);
      }}
    />
  );
};
